import type { StaffRole } from '@planuze/admin-contracts';
import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  InlineAlert,
  Page,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  toast,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { CapabilitiesEditor } from '../components/plans/capabilities-editor.js';
import { planCatalogErrorMessage } from '../components/plans/plan-error.js';
import {
  PublicIdentityFields,
  PublicPricingFields,
} from '../components/plans/plan-form-fields/public-fields.js';
import { usePlanCreateContent } from '../content/sections/plan-create.js';
import { usePlanForm } from '../hooks/use-plan-form.js';
import { useCreatePlan } from '../hooks/use-plans.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'billing_ops'];

/* Criação de plano PÚBLICO em página cheia (ADR-0704) — criar dispara Product + Price no Stripe
   (irreversível), então merece uma tela dedicada em vez de um modal. Seções: identidade, preço,
   capabilities. Só billing_ops/super_admin (canManage). */
export function PlanCreatePage() {
  const copy = usePlanCreateContent();
  const navigate = useNavigate();
  const session = useStaffSession();
  const canManage = MANAGE_ROLES.includes(session?.role ?? 'readonly');
  const form = usePlanForm({});
  const create = useCreatePlan();
  const busy = create.isPending;

  const goToList = (): void => {
    navigate({ to: '/plans' }).catch(() => undefined);
  };

  if (!canManage) {
    return (
      <Page>
        <PageHeader>
          <PageTitleBlock>
            <Breadcrumb
              ariaLabel={copy.breadcrumbAriaLabel}
              items={[
                { label: copy.breadcrumbPlans, onClick: goToList },
                { label: copy.breadcrumbNewPlan },
              ]}
            />
            <PageTitle>{copy.pageTitle}</PageTitle>
          </PageTitleBlock>
        </PageHeader>
        <PageContent>
          <InlineAlert variant="destructive">{copy.forbiddenMessage}</InlineAlert>
        </PageContent>
      </Page>
    );
  }

  const submit = (): void => {
    create.mutate(form.createInput, {
      onSuccess: () => {
        toast.success(copy.createSuccessToast);
        goToList();
      },
      onError: (err) => toast.error(planCatalogErrorMessage(err)),
    });
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <Breadcrumb
            ariaLabel={copy.breadcrumbAriaLabel}
            items={[
              { label: copy.breadcrumbPlans, onClick: goToList },
              { label: copy.breadcrumbNewPlan },
            ]}
          />
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
      </PageHeader>
      <PageContent className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{copy.identityCardTitle}</CardTitle>
            <CardDescription>{copy.identityCardDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <PublicIdentityFields form={form} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{copy.pricingCardTitle}</CardTitle>
            <CardDescription>{copy.pricingCardDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <PublicPricingFields form={form} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{copy.capabilitiesCardTitle}</CardTitle>
            <CardDescription>{copy.capabilitiesCardDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <CapabilitiesEditor
              value={form.capabilities}
              onChange={form.setCapabilities}
              allowSeats={false}
            />
          </CardContent>
        </Card>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={goToList} disabled={busy}>
            {copy.cancelButton}
          </Button>
          <Button loading={busy} disabled={!form.valid} onClick={submit}>
            {copy.createButton}
          </Button>
        </div>
      </PageContent>
    </Page>
  );
}
